import type {Permission} from "./permission-types.ts";
import type {PermissionProviderResultProps} from "./types.ts";
import {usePermission} from "./use-permission.ts";

type Props = Readonly<{
    // The permission to check.
    permission: Permission;
    // A function that will be called with the result of the permission
    // check.
    children: (props: PermissionProviderResultProps) => React.ReactElement;
}>;

/**
 * Provides access to a user's detailed permissions in an SSR-safe way.
 *
 * Example usage:
 * render(): React.Node {
 *   return <PermissionsProvider name="can_publish">
 *     {({loading, error, hasPermission}) => {
 *       if (loading) {
 *         return <Spinner />;
 *       } else if (error) {
 *         return <ErrorMessage />;
 *       } else if (hasPermission) {
 *         return ... // display something for a user who has permission
 *       } else {
 *         return ... // display something for a user who DOESN'T have permission
 *       }
 *     }}
 *   </PermissionsProvider>;
 * }
 *
 * This component takes a render prop for children that contains
 * thre props: `loading`, `error`, and `hasPermission`.  If `loading` is
 * true, the other two props will be undefined.  If `loading` is
 * false, either `error` or `hasPermission` will be defined.
 */
export const PermissionProvider = ({
    permission,
    children,
}: Props): React.ReactElement => {
    const result = usePermission({permission});
    return children(result);
};
